// eslint-disable-next-line no-restricted-imports
import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Combobox,
  Flex,
  GridAlpha,
  Heading,
  Link,
  PhoneInput,
  Select,
  Text,
  TextInput,
  createForm,
  createPhoneInputValidator,
  useId,
  useToasts,
} from '@applyboard/crystal-ui';
import { TextAlign } from '@applyboard/crystal-ui/lib/types/internal/InternalText';
import { ArrowRightOutlineIcon } from '@applyboard/ui-icons';
import { useEventTracker, useTranslation } from '@applyboard/ui-utils';
import TextInputWithFilter, {
  getForbiddenCharsInString,
} from '../../../../common/components/TextInputWithFilter';
import { useGetSource, useGetUtmSource } from '../../../../common/hooks/useQueryParams';
import { zodValidateField } from '../../../../common/schema/forms';
import { htmlDecode } from '../../../../common/utils/decodeHtml';
import { phoneInputValidatorBuilder } from '../../../../common/utils/phoneInputValidatorBuilder';
import {
  LoansError,
  useCreateLoanPartialApplicationV2,
  useGetLoanSupportedSchools,
  useGetSchoolPrograms,
} from '../../../../data/loans';
import { useGetProgramNames } from '../../../../data/programs';
import { useGetSchoolNames } from '../../../../data/schools';
import MandatoryFieldsHelpText from '../../MandatoryFieldsHelpText';
import { FIELD_OF_STUDY_OPTIONS_LIST, FULL_COUNTRY_OPTIONS_LIST } from '../../constants';
import {
  PROGRAM_NAME_REGEX,
  SCHOOL_NAME_REGEX,
  consentToUsePrivateDataSchema,
  destinationSchema,
  fieldOfStudySchema,
  programNameSchema,
  schoolNameSchema,
  studentEmailAddressConfirmationSchema,
  studentEmailAddressSchema,
  studentFirstNameSchema,
  studentLastNameSchema,
  termsAndConditionsSchema,
} from '../../formSchemas';
import { LoansFormEvents } from '../../types';
import { StepperState } from '../LoanFormStepper';
import { LoansHeaderImageDesktopMd } from './assets/HeaderBackgroundDesktopMd';
import { LoansHeaderImageDesktopSmToMd } from './assets/HeaderBackgroundDesktopSmToMd';
import { LoansHeaderImageMobile } from './assets/HeaderBackgroundMobile';
import { PersonalInformationLeadForm } from './types';
const { Field, Form, useFieldValues, useSetFieldValues } =
  createForm<PersonalInformationLeadForm>();

function HeadingTextCopies({ align }: { align?: TextAlign }) {
  const { t } = useTranslation();
  return (
    <>
      <Text contrast="inverted" variant="headlineS" align={align}>
        {t('loans.fieldLabels.bannerCopy-1-1')}
      </Text>
      <Text contrast="inverted" variant="headlineS" align={align}>
        {t('loans.fieldLabels.bannerCopy-1-2')}
      </Text>
      <br />
      <Text contrast="inverted" variant="labelS" align={align}>
        {t('loans.fieldLabels.bannerCopy-2-1')}
      </Text>
      <Text contrast="inverted" variant="labelS" align={align}>
        {t('loans.fieldLabels.bannerCopy-2-2')}
      </Text>
    </>
  );
}

function PersonalInformationLead({ loanData, setLoanData, goNext }: StepperState) {
  const formId = useId();
  const { t } = useTranslation();
  const toasts = useToasts();
  const { trackEvent } = useEventTracker();
  const utmSource = useGetUtmSource();
  const source = useGetSource();
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const { isCreatingPartialLoanApplication, createLoanPartialApplication } =
    useCreateLoanPartialApplicationV2();

  const defaultFormValues: PersonalInformationLeadForm = {
    ...loanData.student,
    nationality: loanData.student.nationality || '',
    destination: loanData.destinationLeadDetails?.destination || '',
    schoolName: loanData.destinationLeadDetails?.schoolName || '',
    programName: loanData.destinationLeadDetails?.programName || '',
    fieldOfStudy: loanData.destinationLeadDetails?.fieldOfStudy || '',
    isSameAsContactPhoneNumber: false,
    contactWhatsAppNumber: loanData.student.contactWhatsAppNumber ?? '',
    emailAddressConfirmation: loanData.student.emailAddress,
    agreeTermsAndCondition: loanData.agreementAndConsent?.agreeTermsAndCondition ?? false,
    consentToUsePrivateData: loanData.agreementAndConsent?.consentToUsePrivateData ?? false,
  };

  const submitCallback = useCallback(
    (values: PersonalInformationLeadForm) => {
      loanData.student = {
        firstName: values.firstName,
        lastName: values.lastName,
        emailAddress: values.emailAddress,
        contactPhoneNumber: values.contactPhoneNumber,
        contactWhatsAppNumber:
          values.contactWhatsAppNumber === '' ? undefined : values.contactWhatsAppNumber,
        nationality: values.nationality,
      };

      loanData.destinationLeadDetails = {
        destination: values.destination,
        schoolName: values.schoolName,
        programName: values.programName,
        fieldOfStudy: values.fieldOfStudy,
      };

      loanData.agreementAndConsent = {
        agreeTermsAndCondition: values.agreeTermsAndCondition,
        consentToUsePrivateData: values.consentToUsePrivateData,
      };

      // validate loan data here
      setLoanData?.(loanData);

      trackEvent({
        event: LoansFormEvents.StartedForm,
        data: { rp_id: loanData.recruitmentPartnerId, utm_source: utmSource, source: source },
      });

      setSubmitDisabled(true);

      createLoanPartialApplication(
        {
          student_personal_information: {
            given_name: loanData.student.firstName,
            last_name: loanData.student.lastName,
            email_address: loanData.student.emailAddress,
            phone_number: loanData.student.contactPhoneNumber.replace('+', ''),
            whatsapp_number: loanData.student.contactWhatsAppNumber?.replace('+', ''),
            nationality: loanData.student.nationality,
          },
          destination_information: {
            destination_country: loanData.destinationLeadDetails.destination,
            school_name: loanData.destinationLeadDetails.schoolName,
            program_name: loanData.destinationLeadDetails.programName,
            field_of_study: loanData.destinationLeadDetails.fieldOfStudy,
          },
          privacy_consent: loanData.agreementAndConsent.consentToUsePrivateData,
          terms_and_conditions_consent: loanData.agreementAndConsent.agreeTermsAndCondition,
          rpId: loanData.recruitmentPartnerId,
          source: loanData.source,
        },
        result => {
          console.log('Loan partial application successfully submitted', result.data.id);
          setSubmitDisabled(false);
          setLoanData({ ...loanData, id: result.data.id }); // sets the loanId coming from BE in the state
          trackEvent({
            event: LoansFormEvents.LeadSubmissionCompleted,
            data: { rp_id: loanData.recruitmentPartnerId, utm_source: utmSource, source: source },
          });
          goNext();
        },
        error => {
          console.error('Failed to submit loan partial application', error);

          let errorMessage = t('common.errors.submission');
          if (error instanceof LoansError) {
            const loansError = error as LoansError;
            errorMessage =
              loansError.errorBody.message instanceof Array
                ? loansError.errorBody.message.join(',')
                : loansError.errorBody.message;
            if (loansError.errorBody?.errors?.find(err => err.status === '409')) {
              toasts.negative(new Error(t('loans.errors.duplicateApplication')));
              trackEvent({
                event: LoansFormEvents.CheckDuplicateErrored,
                data: {
                  rp_id: loanData.recruitmentPartnerId,
                  utm_source: utmSource,
                  source: source,
                },
                error: new Error(errorMessage),
              });
            } else {
              toasts.negative(new Error(errorMessage));
            }
          } else {
            toasts.negative(new Error('Failed to submit loan application'));
          }
          const errorReason = new Error(errorMessage);
          trackEvent({
            event: LoansFormEvents.LeadSubmissionErrored,
            data: { rp_id: loanData.recruitmentPartnerId, utm_source: utmSource, source: source },
            error: errorReason,
          });
          setSubmitDisabled(false);
        },
      );
    },
    [
      loanData,
      setLoanData,
      trackEvent,
      utmSource,
      source,
      toasts,
      goNext,
      t,
      createLoanPartialApplication,
    ],
  );

  return (
    <>
      <div
        style={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: 'repeat(27, 1fr)',
          paddingBottom: '24px',
        }}
      >
        <div style={{ gridColumn: '1 / -1' }}>
          <Box hideBelow="md">
            <LoansHeaderImageDesktopMd />
          </Box>
          <Box hideBelow="sm" hideAbove="md">
            <LoansHeaderImageDesktopSmToMd />
          </Box>
          <Box hideAbove="sm">
            <LoansHeaderImageMobile />
          </Box>
        </div>

        <Box hideBelow="md">
          <div
            style={{
              position: 'absolute',
              top: '45%',
              left: '0',
              transform: 'translateY(-50%)',
              gridColumn: '3 / 26',
            }}
          >
            <HeadingTextCopies />
          </div>
        </Box>
        <Box hideBelow="sm" hideAbove="md">
          <div
            style={{
              position: 'absolute',
              top: '40%',
              left: '0',
              width: '100%',
              transform: 'translateY(-50%)',
              gridColumn: '2 / 27',
            }}
          >
            <HeadingTextCopies />
          </div>
        </Box>
        <Box hideAbove="sm">
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '0',
              width: '100%',
              transform: 'translateY(-50%)',
              gridColumn: '2 / 27',
            }}
          >
            <HeadingTextCopies align="center" />
          </div>
        </Box>
      </div>

      <Form id={formId} defaultValues={defaultFormValues} onSubmit={submitCallback}>
        <GridAlpha columns="repeat(27, 1fr)" rowGap={6} pb={6}>
          <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 26 }}>
            <Flex pt={{ sm: 0, md: 6 }} pb={{ sm: 0, md: 4 }} align="center" direction="column">
              <Heading level={1} align="center" variant={{ sm: 'headlineS', md: 'headlineM' }}>
                {t('loans.fieldLabels.headingCopy-1')}
              </Heading>
              <Text
                contrast="mid"
                intent="secondary"
                variant={{ sm: 'bodyM', md: 'bodyL' }}
                align="center"
              >
                {t('loans.fieldLabels.headingCopy-2')}
              </Text>
            </Flex>
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 14 }}>
            <Field
              appearance="native"
              as={Select}
              name="nationality"
              label={t('loans.fieldLabels.nationality')}
              required={t('loans.errors.nationalityRequired')}
              validate={(value, { triggerValidation, values }) => {
                const formValues: PersonalInformationLeadForm =
                  values as PersonalInformationLeadForm;

                if (formValues.schoolName) {
                  triggerValidation('schoolName');
                }

                return true;
              }}
            >
              {FULL_COUNTRY_OPTIONS_LIST.map(opt => (
                <Select.Option {...opt} key={opt.value} />
              ))}
            </Field>
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={{ xs: 2, md: 15 }} columnEnd={{ xs: 27, md: 26 }}>
            <Field
              appearance="native"
              as={Select}
              name="destination"
              label={t('loans.fieldLabels.destination')}
              required={t('loans.errors.destinationRequired')}
              validate={value => {
                return zodValidateField(destinationSchema(t), 'destination', value, toasts);
              }}
            >
              {FULL_COUNTRY_OPTIONS_LIST.map(opt => (
                <Select.Option {...opt} key={opt.value} />
              ))}
            </Field>
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 14 }}>
            <SchoolField />
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={{ xs: 2, md: 15 }} columnEnd={{ xs: 27, md: 26 }}>
            <ProgramField />
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 14 }}>
            <TextInputWithFilter
              Field={Field}
              fieldName="firstName"
              fieldLabel={t('loans.fieldLabels.studentFirstName')}
              required={t('loans.errors.studentFirstNameRequired')}
              zodSchema={studentFirstNameSchema}
            />
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={{ xs: 2, md: 15 }} columnEnd={{ xs: 27, md: 26 }}>
            <TextInputWithFilter
              Field={Field}
              fieldName="lastName"
              fieldLabel={t('loans.fieldLabels.studentLastName')}
              zodSchema={studentLastNameSchema}
            />
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 14 }}>
            <Field
              as={TextInput}
              name="emailAddress"
              label={t('loans.fieldLabels.studentEmailAddress')}
              required={t('loans.errors.emailRequired')}
              validate={value => {
                return zodValidateField(
                  studentEmailAddressSchema(t),
                  'emailAddress',
                  value,
                  toasts,
                );
              }}
            />
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={{ xs: 2, md: 15 }} columnEnd={{ xs: 27, md: 26 }}>
            <Field
              as={TextInput}
              name="emailAddressConfirmation"
              label={t('loans.fieldLabels.studentEmailAddressConfirmation')}
              required={t('loans.errors.confirmEmailRequired')}
              validate={(value, { values }) => {
                if (value !== values.emailAddress) {
                  return t('loans.errors.emailShouldMatch');
                }
                return zodValidateField(
                  studentEmailAddressConfirmationSchema(t),
                  'emailAddressConfirmation',
                  value,
                  toasts,
                );
              }}
            />
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 14 }}>
            <PhoneFieldWrapper />
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={{ xs: 2, md: 15 }} columnEnd={{ xs: 27, md: 26 }}>
            <WhatsAppFieldWrapper />
            <SameAsPhoneNumberCheckbox />
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 14 }}>
            <Field
              appearance="native"
              as={Select}
              name="fieldOfStudy"
              label={t('loans.fieldLabels.fieldOfStudy')}
              required={t('loans.errors.fieldOfStudyRequired')}
              validate={value => {
                return zodValidateField(fieldOfStudySchema(t), 'fieldOfStudy', value, toasts);
              }}
            >
              {FIELD_OF_STUDY_OPTIONS_LIST.map(opt => (
                <Select.Option {...opt} key={opt.value} />
              ))}
            </Field>
          </GridAlpha.Item>

          <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 26 }}>
            <Flex p={0} direction="column" gap={4}>
              <Field
                as={Checkbox}
                name="agreeTermsAndCondition"
                label={
                  <span>
                    <span style={{ color: '#C01C48' }}> * </span>
                    {t('loans.agreementAndConsent.consentToUsePrivateDataLabel-1')}
                    <Link
                      href="https://www.applyboard.com/services/%E2%80%9Dhttps://www.applyboard.com/privacy-and-cookie-policy"
                      mode="override_user_choice_and_open_in_new_tab"
                    >
                      {t('loans.agreementAndConsent.consentToUsePrivateDataLabel-2')}
                    </Link>
                    {t('loans.agreementAndConsent.consentToUsePrivateDataLabel-3')}
                  </span>
                }
                required={t('loans.agreementAndConsent.errors.consentToUsePrivateDataRequired')}
                disabled={isCreatingPartialLoanApplication}
                validate={value => {
                  return zodValidateField(
                    termsAndConditionsSchema(t),
                    'agreeTermsAndCondition',
                    value,
                    toasts,
                  );
                }}
              />
              <Field
                as={Checkbox}
                name="consentToUsePrivateData"
                label={
                  <span>
                    <span style={{ color: '#C01C48' }}> * </span>
                    {t('loans.agreementAndConsent.agreeTermsAndConditionLabel-1')}
                    <Link
                      href="https://www.applyboard.com/services/%E2%80%9Dhttps://www.applyboard.com/our-solutions/loan-terms-conditions"
                      mode="override_user_choice_and_open_in_new_tab"
                    >
                      {t('loans.agreementAndConsent.agreeTermsAndConditionLabel-2')}
                    </Link>
                  </span>
                }
                required={t('loans.agreementAndConsent.errors.termsAndConditionsRequired')}
                disabled={isCreatingPartialLoanApplication}
                validate={value => {
                  return zodValidateField(
                    consentToUsePrivateDataSchema(t),
                    'consentToUsePrivateData',
                    value,
                    toasts,
                  );
                }}
              />
            </Flex>
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={{ xs: 27, md: 26 }}>
            <MandatoryFieldsHelpText />
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={{ xs: 2, md: 3 }} columnEnd={26}>
            <Flex justify="end">
              <Button
                intent="primary"
                size="lg"
                width="hug"
                trailIcon={ArrowRightOutlineIcon}
                type="submit"
                form={formId}
                disabled={submitDisabled}
              >
                {t('loans.submitAndContinue')}
              </Button>
            </Flex>
          </GridAlpha.Item>
        </GridAlpha>
      </Form>
    </>
  );
}

function WhatsAppFieldWrapper() {
  const { isSameAsContactPhoneNumber } = useFieldValues(['isSameAsContactPhoneNumber']);
  const { t } = useTranslation();
  return (
    <Field
      as={PhoneInput}
      name="contactWhatsAppNumber"
      label={t('loans.fieldLabels.contactWhatsAppNumber')}
      required={false}
      disabled={isSameAsContactPhoneNumber}
      validate={phoneInputValidatorBuilder(true)}
    />
  );
}

function PhoneFieldWrapper() {
  const setFormValues = useSetFieldValues();
  const { isSameAsContactPhoneNumber } = useFieldValues(['isSameAsContactPhoneNumber']);
  const { t } = useTranslation();
  return (
    <Field
      as={PhoneInput}
      name="contactPhoneNumber"
      label={t('loans.fieldLabels.contactPhoneNumber')}
      required={t('loans.errors.phoneNumberRequired')}
      validate={createPhoneInputValidator()}
      onChange={val => {
        if (isSameAsContactPhoneNumber) {
          setFormValues({
            contactWhatsAppNumber: val,
          });
        }
      }}
    />
  );
}

function SameAsPhoneNumberCheckbox() {
  const setFormValues = useSetFieldValues();
  const { contactPhoneNumber } = useFieldValues(['contactPhoneNumber']);
  return (
    <Field
      as={Checkbox}
      name="isSameAsContactPhoneNumber"
      label="Same as Phone Number"
      labelPlacement="right"
      onChange={value => {
        if (value) {
          setFormValues({
            contactWhatsAppNumber: contactPhoneNumber,
          });
        }
      }}
    />
  );
}

function SchoolField() {
  const formValues = useFieldValues(['nationality']);

  if (formValues.nationality === 'IN') {
    return <IndiaSchoolField />;
  }

  return <RowSchoolField />;
}

function IndiaSchoolField() {
  const fieldName = 'schoolName';
  const setFieldValues = useSetFieldValues();
  const { t } = useTranslation();
  const toasts = useToasts();
  const [schoolQuery, setSchoolQuery] = useState('');
  const [debouncedSchoolQuery, setDebouncedSchoolQuery] = useState('');
  const canQuerySchool = debouncedSchoolQuery.length >= 3;
  const { schools, isLoadingSchools } = useGetSchoolNames(
    { name: debouncedSchoolQuery },
    { enabled: canQuerySchool },
  );

  useEffect(() => {
    const timerId = setTimeout(() => setDebouncedSchoolQuery(schoolQuery), 600);
    return () => clearTimeout(timerId);
  }, [schoolQuery]);

  const clearProgramName = () => {
    setFieldValues({
      programName: '',
    });
  };

  return (
    <>
      <Field
        as={Combobox}
        name={fieldName}
        label={t('loans.fieldLabels.schoolName')}
        required={t('loans.errors.schoolNameRequired')}
        size="md"
        type="text"
        valueMode="input"
        onSearch={setSchoolQuery}
        onChange={() => clearProgramName()}
        loading={isLoadingSchools}
        placeholder="Start typing to see the suggestions"
        filterOption={() => true}
        validate={(value: string) => {
          const zodValidationResult = zodValidateField(
            schoolNameSchema(t),
            fieldName,
            value,
            toasts,
          );

          if (zodValidationResult === t('common.errors.specialCharactersNotAllowed')) {
            const forbiddenInValue = getForbiddenCharsInString(SCHOOL_NAME_REGEX, value);
            const errorMessage = t('common.errors.specialCharactersNotAllowed', {
              fieldLabel: fieldName,
              characters: forbiddenInValue.join(' '),
            });
            return htmlDecode(errorMessage);
          }

          return true;
        }}
      >
        {schools?.map(school => (
          <Combobox.Option key={school.id} label={school.name} value={school.name} />
        ))}
      </Field>
      {!canQuerySchool && <Text>Please type more so that we can provide suggestions</Text>}
    </>
  );
}

function RowSchoolField() {
  const fieldName = 'schoolName';
  const { t } = useTranslation();
  const toasts = useToasts();
  const { trackEvent } = useEventTracker();
  const { supportedSchools, isLoadingSupportedSchools } = useGetLoanSupportedSchools();
  const uniquedSupportedSchools = [...new Set(supportedSchools)];
  const uniqueSupportedSchoolsInLowerCase = uniquedSupportedSchools.map(school =>
    school.toLowerCase(),
  );

  return (
    <>
      <Field
        as={Combobox}
        name={fieldName}
        label={t('loans.fieldLabels.schoolName')}
        required={t('loans.errors.schoolNameRequired')}
        size="md"
        type="text"
        valueMode="input"
        loading={isLoadingSupportedSchools}
        placeholder="Start typing to see the suggestions"
        filterOption={(option: { label: string }, query: string) => {
          return option.label.toLowerCase().includes(query.toLowerCase());
        }}
        validate={(value: string, { values }) => {
          if (!uniqueSupportedSchoolsInLowerCase.includes(value.toLowerCase())) {
            trackEvent({
              event: LoansFormEvents.InvalidSchoolName,
              data: {
                nationality: values.nationality,
                schoolName: value,
              },
            });

            return 'Based on your current nationality and school selection, you are not eligible to proceed with a loan application';
          }

          const zodValidationResult = zodValidateField(
            schoolNameSchema(t),
            fieldName,
            value,
            toasts,
          );

          if (zodValidationResult === t('common.errors.specialCharactersNotAllowed')) {
            const forbiddenInValue = getForbiddenCharsInString(SCHOOL_NAME_REGEX, value);
            const errorMessage = t('common.errors.specialCharactersNotAllowed', {
              fieldLabel: fieldName,
              characters: forbiddenInValue.join(' '),
            });
            return htmlDecode(errorMessage);
          }

          return true;
        }}
      >
        {uniquedSupportedSchools.map(school => (
          <Combobox.Option key={school} label={school} value={school} />
        ))}
      </Field>
    </>
  );
}

function ProgramField() {
  const formValues = useFieldValues(['nationality']);

  if (formValues.nationality === 'IN') {
    return <IndiaProgramField />;
  }

  return <NonIndiaProgramField />;
}

function IndiaProgramField() {
  const fieldName = 'programName';
  const formValues = useFieldValues(['schoolName']);
  const { t } = useTranslation();
  const toasts = useToasts();
  const [programQuery, setProgramQuery] = useState('');
  const [debouncedProgramQuery, setDebouncedProgramQuery] = useState('');
  const canQueryProgram = debouncedProgramQuery.length >= 3;
  const { schools, isLoadingSchools } = useGetSchoolNames(
    { name: formValues.schoolName },
    { enabled: canQueryProgram },
  );
  // Make sure that we're getting the right school and not a partial match.
  // There is no penalty in querying it again because it's cached.
  // We are doing this to reduce decoupling between the school and program fields so that we can easily move them around
  const schoolId = schools?.[0]?.name === formValues.schoolName ? schools[0].id : '';
  const { programs, isLoadingPrograms } = useGetProgramNames(
    {
      name: debouncedProgramQuery,
      schoolId: schoolId,
    },
    { enabled: canQueryProgram },
  );

  useEffect(() => {
    const timerId = setTimeout(() => setDebouncedProgramQuery(programQuery), 600);
    return () => clearTimeout(timerId);
  }, [programQuery]);

  useEffect(() => setProgramQuery(''), [formValues.schoolName]);

  return (
    <>
      <Field
        as={Combobox}
        name={fieldName}
        label={t('loans.fieldLabels.programName')}
        required={t('loans.errors.programNameRequired')}
        size="md"
        type="text"
        valueMode="input"
        onSearch={setProgramQuery}
        loading={isLoadingPrograms || isLoadingSchools}
        placeholder="Start typing to see the suggestions"
        filterOption={() => true}
        validate={(value: string) => {
          const zodValidationResult = zodValidateField(
            programNameSchema(t),
            fieldName,
            value,
            toasts,
          );

          if (zodValidationResult === t('common.errors.specialCharactersNotAllowed')) {
            const forbiddenInValue = getForbiddenCharsInString(PROGRAM_NAME_REGEX, value);
            const errorMessage = t('common.errors.specialCharactersNotAllowed', {
              fieldLabel: fieldName,
              characters: forbiddenInValue.join(' '),
            });
            return htmlDecode(errorMessage);
          }

          return true;
        }}
      >
        {programs?.map(program => (
          <Combobox.Option key={program.id} label={program.name} value={program.name} />
        ))}
      </Field>
      {!canQueryProgram && <Text>Please type more so that we can provide suggestions</Text>}
    </>
  );
}

function NonIndiaProgramField() {
  const fieldName = 'programName';
  const { t } = useTranslation();
  const toasts = useToasts();
  const formValues = useFieldValues();

  // Use the hook to get programs for the selected school
  const { programs, isLoadingPrograms, programsError } = useGetSchoolPrograms(
    formValues.schoolName,
  );

  // Log errors for debugging purposes
  if (programsError) {
    console.error('Failed to load programs for school:', formValues.schoolName, programsError);
  }

  // Check if we should show dropdown (school selected)
  const shouldShowDropdown = !!formValues.schoolName;

  // Validation function that only validates when there's a value
  const validateProgramField = (value: string) => {
    // If no value, don't validate (prevents premature validation errors)
    if (!value) {
      return true;
    }
    return zodValidateField(programNameSchema(t), fieldName, value, toasts);
  };

  if (shouldShowDropdown) {
    // Show dropdown with programs
    return (
      <Field
        as={Combobox}
        name={fieldName}
        label={t('loans.fieldLabels.programName')}
        required={t('loans.errors.programNameRequired')}
        size="md"
        type="text"
        valueMode="input"
        loading={isLoadingPrograms}
        placeholder="Start typing to see the suggestions"
        filterOption={(option: { label: string }, query: string) => {
          return option.label.toLowerCase().includes(query.toLowerCase());
        }}
        validate={validateProgramField}
      >
        {programs.map(program => (
          <Combobox.Option key={program} label={program} value={program} />
        ))}
      </Field>
    );
  }

  // Show text input for no school selected
  return (
    <Field
      as={TextInput}
      name={fieldName}
      label={t('loans.fieldLabels.programName')}
      required={t('loans.errors.programNameRequired')}
      validate={(value: string) => {
        // First check for special characters
        if (value && !PROGRAM_NAME_REGEX.test(value)) {
          const forbiddenInValue = getForbiddenCharsInString(PROGRAM_NAME_REGEX, value);
          const errorMessage = t('common.errors.specialCharactersNotAllowed', {
            fieldLabel: fieldName,
            characters: forbiddenInValue.join(' '),
          });
          return htmlDecode(errorMessage);
        }

        // Then validate with custom function
        return validateProgramField(value);
      }}
    />
  );
}

export default PersonalInformationLead;
